import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, ThumbsUp, FileText, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import NewQuoteModal from "@/components/NewQuoteModal";
import EditQuoteModal from "@/components/EditQuoteModal";
import { EtiquetasChips } from "@/components/Etiquetas";
import { ReorderableTable, type ReorderableColumn } from "@/components/ReorderableTable";

export default function Quotes() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useGoogleAuth();
  
  // Estados
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFiltro, setStatusFiltro] = useState<string | undefined>(undefined);
  const [emitenteIdsTemporarios, setEmitenteIdsTemporarios] = useState<Set<string>>(new Set());
  const [emitenteIdsAplicados, setEmitenteIdsAplicados] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("numero");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [editingAnotacoes, setEditingAnotacoes] = useState<number | null>(null);
  const [anotacoesValue, setAnotacoesValue] = useState("");
  const [isNewQuoteModalOpen, setIsNewQuoteModalOpen] = useState(false);
  const [isEditQuoteModalOpen, setIsEditQuoteModalOpen] = useState(false);
  const [selectedOrcamentoId, setSelectedOrcamentoId] = useState<number>(0);
  
  const itemsPerPage = 10;
  
  // Queries
  const { data: orcamentos = [], refetch } = trpc.orcamentos.list.useQuery(
    statusFiltro ? { status: statusFiltro } : undefined
  );
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const selectedOrcamentoIsFinal = (orcamentos as any[]).some(
    (orc: any) => orc.id === selectedOrcamentoId && ["convertido", "excluido"].includes(orc.status)
  );
  
  // Mutations
  const updateMutation = trpc.orcamentos.update.useMutation({
    onSuccess: () => {
      toast.success("Orçamento atualizado");
      setEditingAnotacoes(null);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar");
    },
  });
  
  const deleteMutation = trpc.orcamentos.deleteMultiple.useMutation({
    onSuccess: () => {
      toast.success("Orçamentos excluídos");
      setSelectedIds([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao excluir");
    },
  });
  
  
  const aprovarMutation = trpc.orcamentos.aprovar.useMutation({
    onSuccess: (data) => {
      toast.success(`Orçamento aprovado e convertido em pedido #${data.pedidoId}!`);
      refetch();
    },
    onError: (error) => {
      toast.error(`Erro ao aprovar: ${error.message}`);
    },
  });
  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      setLocation("/");
    }
  }, [user, authLoading, setLocation]);
  
  // Reset para página 1 quando busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFiltro, emitenteIdsAplicados, sortColumn, sortDirection]);

  const emitentesDisponiveis = emitentes
    .map((emitente: any) => ({
      id: String(emitente.id),
      nome: emitente.nomeFantasia || emitente.razaoSocial || `Emitente #${emitente.id}`,
    }))
    .sort((a: any, b: any) => a.nome.localeCompare(b.nome, "pt-BR"));
  const todosEmitentesSelecionados = emitentesDisponiveis.length > 0 &&
    emitentesDisponiveis.every((emitente: any) => emitenteIdsTemporarios.has(emitente.id));
  
  // Filtrar orçamentos
  const filteredOrcamentos = orcamentos.filter((orc: any) => {
    const search = searchTerm.toLowerCase();
    const correspondeEmitente = emitenteIdsAplicados.size === 0 ||
      emitenteIdsAplicados.has(String(orc.emitenteId));
    return (
      (!statusFiltro || orc.status === statusFiltro) &&
      correspondeEmitente &&
      (
      orc.numero?.toString().includes(search) ||
      orc.destinatarioNome?.toLowerCase().includes(search) ||
      orc.status?.toLowerCase().includes(search) ||
      orc.anotacoes?.toLowerCase().includes(search) ||
      orc.tipoOrcamentoNome?.toLowerCase().includes(search) ||
      orc.vendedorNome?.toLowerCase().includes(search) ||
      orc.tabelaNome?.toLowerCase().includes(search)
      )
    );
  });

  const getQuoteSortValue = (orc: any, column: string) => {
    switch (column) {
      case "numero": return Number(orc.numero || 0);
      case "valor": return Number(orc.valor || 0);
      case "criado": return orc.criadoEm ? new Date(orc.criadoEm).getTime() : 0;
      case "alterado": return orc.atualizadoEm ? new Date(orc.atualizadoEm).getTime() : 0;
      default: return String(orc[column === "destinatario" ? "destinatarioNome" : column === "tipo" ? "tipoOrcamentoNome" : column === "tabela" ? "tabelaNome" : column === "vendedor" ? "vendedorNome" : column] || "");
    }
  };

  const sortedOrcamentos = [...filteredOrcamentos].sort((a: any, b: any) => {
    const aValue = getQuoteSortValue(a, sortColumn);
    const bValue = getQuoteSortValue(b, sortColumn);
    const comparison = typeof aValue === "number" && typeof bValue === "number"
      ? aValue - bValue
      : String(aValue).localeCompare(String(bValue), "pt-BR", { numeric: true, sensitivity: "base" });
    return sortDirection === "asc" ? comparison : -comparison;
  });

  const toggleQuoteSort = (column: string) => {
    if (sortColumn === column) setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  function toggleEmitenteTemporario(emitenteId: string) {
    setEmitenteIdsTemporarios((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(emitenteId)) proximos.delete(emitenteId);
      else proximos.add(emitenteId);
      return proximos;
    });
  }

  function toggleTodosEmitentes() {
    setEmitenteIdsTemporarios(
      todosEmitentesSelecionados
        ? new Set()
        : new Set(emitentesDisponiveis.map((emitente: any) => emitente.id))
    );
  }

  function aplicarFiltroEmitentes() {
    setEmitenteIdsAplicados(new Set(emitenteIdsTemporarios));
  }
  
  // Paginação
  const totalPages = Math.ceil(sortedOrcamentos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrcamentos = sortedOrcamentos.slice(startIndex, startIndex + itemsPerPage);
  const { data: etiquetasOrcamentos = {} as Record<number, any[]>, refetch: refetchEtiquetasOrcamentos } = trpc.etiquetas.porEntidades.useQuery({
    modulo: "orcamento",
    entidadeIds: paginatedOrcamentos.map((orcamento: any) => orcamento.id),
  });

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }
  
  if (!user) return null;

  // Handlers
  const orcamentosRemoviveis = paginatedOrcamentos.filter(
    (orc: any) => !["aprovado", "convertido", "excluido"].includes(orc.status)
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(orcamentosRemoviveis.map((o: any) => o.id));
    } else {
      setSelectedIds([]);
    }
  };
  
  const handleSelectOne = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    }
  };
  
  const handleEditAnotacoes = (orc: any) => {
    setEditingAnotacoes(orc.id);
    setAnotacoesValue(orc.anotacoes || "");
  };
  
  const handleSaveAnotacoes = () => {
    if (editingAnotacoes) {
      updateMutation.mutate({
        id: editingAnotacoes,
        anotacoes: anotacoesValue,
      });
    }
  };
  
  const handleCancelAnotacoes = () => {
    setEditingAnotacoes(null);
    setAnotacoesValue("");
  };
  
  const handleRemoveSelected = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um orçamento");
      return;
    }
    
    if (confirm(`Excluir ${selectedIds.length} orçamento(s)? Eles permanecerão visíveis, mas não poderão mais ser alterados.`)) {
      deleteMutation.mutate({ ids: selectedIds });
    }
  };
  
  const handleAprovar = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Selecione pelo menos um orçamento para aprovar");
      return;
    }

    for (const orcamentoId of selectedIds) {
      try {
        await aprovarMutation.mutateAsync({ orcamentoId });
      } catch (error) {
        // Erro já tratado no onError
      }
    }
    
    setSelectedIds([]);
  };
  
  const handleRelatorio = () => {
    toast.info("Função de relatório em desenvolvimento");
  };
  
  const handleNovo = () => {
    setIsNewQuoteModalOpen(true);
  };
  
  // Status badge colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case "aprovado":
        return "bg-green-100 text-green-800";
      case "reprovado":
        return "bg-red-100 text-red-800";
      case "reserva":
        return "bg-yellow-100 text-yellow-800";
      case "convertido":
        return "bg-gray-100 text-gray-800";
      case "excluido":
        return "bg-red-100 text-red-800";
      case "rascunho":
        return "bg-slate-100 text-slate-600";
      case "pendente":
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const quoteColumns: ReorderableColumn<any>[] = [
    { id: "selecionar", fixed: true, header: orcamentosRemoviveis.length > 0 && <Checkbox checked={orcamentosRemoviveis.every((orc: any) => selectedIds.includes(orc.id))} onCheckedChange={handleSelectAll} />, headerClassName: "p-3 text-left", className: "p-3", renderCell: (orc) => <span onClick={(event) => event.stopPropagation()}>{!["aprovado", "convertido", "excluido"].includes(orc.status) && <Checkbox checked={selectedIds.includes(orc.id)} onCheckedChange={(checked) => handleSelectOne(orc.id, checked as boolean)} />}</span> },
    { id: "numero", header: "Número", sortKey: "numero", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => orc.numero },
    { id: "destinatario", header: "Destinatário", sortKey: "destinatario", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => orc.destinatarioNome || "-" },
    { id: "status", header: "Status", sortKey: "status", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3", renderCell: (orc) => <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(orc.status)}`}>{orc.status?.toUpperCase()}</span> },
    { id: "valor", header: "Valor", sortKey: "valor", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => `R$ ${Number(orc.valor || 0).toFixed(2)}` },
    { id: "anotacoes", header: "Anotações", sortKey: "anotacoes", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => <span onClick={(event) => event.stopPropagation()}>{!["convertido", "excluido"].includes(orc.status) && editingAnotacoes === orc.id ? <div className="flex gap-2"><Input value={anotacoesValue} onChange={(event) => setAnotacoesValue(event.target.value)} className="h-8" autoFocus /><Button size="sm" onClick={handleSaveAnotacoes}>Salvar</Button><Button size="sm" variant="outline" onClick={handleCancelAnotacoes}>Cancelar</Button></div> : <span className={["convertido", "excluido"].includes(orc.status) ? "" : "cursor-pointer hover:underline"} onClick={() => { if (!["convertido", "excluido"].includes(orc.status)) handleEditAnotacoes(orc); }}>{orc.anotacoes || "-"}</span>}</span> },
    { id: "etiqueta", header: "Etiqueta", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => <span onClick={(event) => event.stopPropagation()}><EtiquetasChips etiquetas={etiquetasOrcamentos[orc.id]} /></span> },
    { id: "tipo", header: "Tipo de Orçamento", sortKey: "tipo", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => orc.tipoOrcamentoNome || "-" },
    { id: "vendedor", header: "Vendedor", sortKey: "vendedor", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => orc.vendedorNome || "-" },
    { id: "tabela", header: "Tabela", sortKey: "tabela", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => orc.tabelaNome || "-" },
    { id: "criado", header: "Log de Criação", sortKey: "criado", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => <>{orc.criadoEm && new Date(orc.criadoEm).toLocaleString("pt-BR")}<br /><span className="text-xs text-muted-foreground">{orc.criadoPorNome}</span></> },
    { id: "alterado", header: "Log de Alteração", sortKey: "alterado", headerClassName: "p-3 text-left text-sm font-medium", className: "p-3 text-sm", renderCell: (orc) => <>{orc.atualizadoEm && new Date(orc.atualizadoEm).toLocaleString("pt-BR")}<br /><span className="text-xs text-muted-foreground">{orc.atualizadoPorNome}</span></> },
  ];
  
  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Orçamentos</h1>
        <p className="text-muted-foreground">Gerencie orçamentos e seus itens</p>
      </div>
      
      {/* Botões de ação */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Button
          onClick={handleNovo}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Orçamento
        </Button>
        
        <Button
          onClick={handleAprovar}
          variant="outline"
          className="border-blue-600 text-blue-600 hover:bg-blue-50"
        >
          <ThumbsUp className="w-4 h-4 mr-2" />
          Aprovar Orçamentos
        </Button>
        
        <Button
          onClick={handleRelatorio}
          variant="outline"
          className="border-blue-600 text-blue-600 hover:bg-blue-50"
        >
          <FileText className="w-4 h-4 mr-2" />
          Relatório
        </Button>
        
        <Button
          onClick={handleRemoveSelected}
          variant="outline"
          className="border-red-600 text-red-600 hover:bg-red-50"
          disabled={selectedIds.length === 0}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Excluir Selecionados
        </Button>
      </div>
      
      {/* Filtro */}
      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">Filtros:</span>
          <Input
            placeholder="Buscar por qualquer coisa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                {emitenteIdsAplicados.size === 0 || emitenteIdsAplicados.size === emitentesDisponiveis.length
                  ? "Todos os emitentes"
                  : `Emitentes (${emitenteIdsAplicados.size})`}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
              <label className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                <Checkbox
                  checked={todosEmitentesSelecionados}
                  onCheckedChange={toggleTodosEmitentes}
                />
                <span className="text-sm font-medium">Selecionar todos</span>
              </label>
              <div className="my-2 max-h-56 overflow-y-auto border-y py-1">
                {emitentesDisponiveis.map((emitente: any) => (
                  <label key={emitente.id} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                    <Checkbox
                      checked={emitenteIdsTemporarios.has(emitente.id)}
                      onCheckedChange={() => toggleEmitenteTemporario(emitente.id)}
                    />
                    <span className="text-sm">{emitente.nome}</span>
                  </label>
                ))}
              </div>
              <Button size="sm" className="w-full" onClick={aplicarFiltroEmitentes}>
                Aplicar
              </Button>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex flex-wrap gap-2">
            {[
              { value: undefined, label: "Todos" },
              { value: "rascunho", label: "Rascunho" },
              { value: "reserva", label: "Reserva" },
              { value: "aprovado", label: "Aprovado" },
              { value: "reprovado", label: "Reprovado" },
              { value: "pendente", label: "Pendente" },
              { value: "convertido", label: "Convertido" },
              { value: "excluido", label: "Excluído" },
            ].map((filtro) => (
              <Button
                key={filtro.label}
                type="button"
                size="sm"
                variant={statusFiltro === filtro.value ? "default" : "outline"}
                onClick={() => setStatusFiltro(filtro.value)}
              >
                {filtro.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Tabela */}
      <div className="border rounded-lg overflow-auto max-h-[600px]">
        <ReorderableTable
          pageKey="quotes"
          userId={user.id}
          columns={quoteColumns}
          rows={paginatedOrcamentos}
          getRowKey={(orc) => orc.id}
          getRowProps={(orc) => ({ className: "border-t hover:bg-gray-50 cursor-pointer", onClick: () => { setSelectedOrcamentoId(orc.id); setIsEditQuoteModalOpen(true); } })}
          sort={{ column: sortColumn, direction: sortDirection, onToggle: toggleQuoteSort }}
          headClassName="bg-gray-50 sticky top-0"
          emptyMessage={searchTerm ? "Nenhum orçamento encontrado" : "Nenhum orçamento cadastrado"}
          emptyClassName="p-8 text-center text-muted-foreground"
        />
      </div>
      
      {/* Paginação */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Página {currentPage} de {totalPages} ({filteredOrcamentos.length} orçamentos)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
      
      {/* Modal de Novo Orçamento */}
      <NewQuoteModal
        open={isNewQuoteModalOpen}
        onClose={() => setIsNewQuoteModalOpen(false)}
        onSuccess={() => refetch()}
      />
      
      <EditQuoteModal
        open={isEditQuoteModalOpen}
        onClose={() => {
          setIsEditQuoteModalOpen(false);
          setSelectedOrcamentoId(0);
        }}
        onSuccess={() => {
          refetch();
          refetchEtiquetasOrcamentos();
          setIsEditQuoteModalOpen(false);
          setSelectedOrcamentoId(0);
        }}
        orcamentoId={selectedOrcamentoId}
        readOnly={selectedOrcamentoIsFinal}
      />
    </div>
  );
}
